import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import "./BlockKeyword.css";
import { formatISTDateTime } from '../../utils/datetime';
import {usePermission} from "../../hooks/usePermissions";

const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const EditIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);



const DeleteModal = ({ item, onClose, onConfirm, submitting }) => (
  <div
    className="bk-modal-overlay"
  >
    <div className="bk-modal bk-modal-sm">
      <div className="bk-modal-header">
        <h2>Delete Keyword</h2>
        <button
          type="button"
          className="bk-modal-close"
          onClick={onClose}
          disabled={submitting}
        >
          <CloseIcon />
        </button>
      </div>
      <div className="bk-modal-body bk-confirm-content">
        <div className="bk-confirm-icon">Delete</div>
        <p className="bk-confirm-msg">Are you sure you want to delete</p>
        <p className="bk-confirm-name">"{item.keyword}"?</p>
        <p className="bk-confirm-msg bk-error-text">
          This action cannot be undone.
        </p>
      </div>
      <div className="bk-modal-footer bk-flex-center">
        <button
          type="button"
          className="bk-btn-cancel"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="bk-btn-danger"
          onClick={() => onConfirm(item.id)}
          disabled={submitting}
        >
          {submitting ? "Deleting..." : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

const AddEditModal = ({ mode, initialData, onClose, onSave, submitting }) => {
  const [form, setForm] = useState({
    keyword: initialData?.keyword || "",
    action_type: initialData?.action_type || "Blocked",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      keyword: form.keyword.trim(),
      action_type: form.action_type,
    });
  };

  return (
    <div
      className="bk-modal-overlay"
    >
      <div className="bk-modal">
        <div className="bk-modal-header">
          <h2>{mode === "edit" ? "Edit Keyword" : "Add New Keyword"}</h2>
          <button
            type="button"
            className="bk-modal-close"
            onClick={onClose}
            disabled={submitting}
          >
            <CloseIcon />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="bk-modal-body">
            <div className="bk-form-row">
              <div className="bk-form-group">
                <label>
                  Keyword <span className="required">*</span>
                </label>
                <input
                  className="bk-input"
                  type="text"
                  required
                  value={form.keyword}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, keyword: e.target.value }))
                  }
                  placeholder="e.g. spam"
                />
              </div>
              <div className="bk-form-group">
                <label>
                  Action Type <span className="required">*</span>
                </label>
                <select
                  className="bk-select"
                  required
                  value={form.action_type}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      action_type: e.target.value,
                    }))
                  }
                >
                  <option value="Blocked">Blocked</option>
                  <option value="Warn">Warn</option>
                </select>
              </div>
            </div>
          </div>
          <div className="bk-modal-footer">
            <button
              type="button"
              className="bk-btn-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="bk-btn-save" disabled={submitting}>
              {submitting
                ? mode === "edit"
                  ? "Updating..."
                  : "Saving..."
                : mode === "edit"
                  ? "Update Keyword"
                  : "Save Keyword"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BlockKeyword = () => {
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [sortKey, setSortKey] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalMode, setModalMode] = useState(null);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const {hasPermission} = usePermission();

  const itemsPerPage = 8;

  const fetchKeywords = async () => {
    try {
      setLoading(true);
      const response = await api.get("/block", {
        params: {
          search: appliedSearch || undefined,
          action_type: actionFilter !== "all" ? actionFilter : undefined,
          sort_by: sortKey,
          sort_dir: sortDir,
        },
      });
      setKeywords(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      toast.error(error?.message || "Failed to load blocked keywords");
      setKeywords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeywords();
  }, [appliedSearch, actionFilter, sortKey, sortDir]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortKey, sortDir, appliedSearch, actionFilter]);

  const filtered = useMemo(() => keywords, [keywords]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginatedKeywords = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortKey(key);
    setSortDir(key === "id" ? "asc" : "desc");
  };

  const sortIcon = (key) =>
    sortKey === key ? (sortDir === "asc" ? "▲" : "▼") : "⇅";

  const handleSearch = () => {
    setAppliedSearch(searchQuery);
  };

  const handleSearchKey = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleCreate = async (payload) => {
    if (!payload.keyword) {
      toast.error("Keyword is required");
      return;
    }

    try {
      setSaving(true);
      const response = await api.post("/block", payload);
      setKeywords((prev) => [response.data, ...prev]);
      setModalMode(null);
      toast.success("Keyword added successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to add keyword");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload) => {
    if (!selectedKeyword?.id) return;
    if (!payload.keyword) {
      toast.error("Keyword is required");
      return;
    }

    try {
      setSaving(true);
      const response = await api.put(`/block/${selectedKeyword.id}`, payload);
      setKeywords((prev) =>
        prev.map((item) => (item.id === selectedKeyword.id ? response.data : item))
      );
      setModalMode(null);
      setSelectedKeyword(null);
      toast.success("Keyword updated successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to update keyword");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePrompt = (keyword) => {
    setSelectedKeyword(keyword);
    setModalMode("delete");
  };

  const handleEditPrompt = (keyword) => {
    setSelectedKeyword(keyword);
    setModalMode("edit");
  };

  const handleDeleteConfirm = async (id) => {
    try {
      setDeleting(true);
      await api.delete(`/block/${id}`);
      setKeywords((prev) => prev.filter((item) => item.id !== id));
      setModalMode(null);
      setSelectedKeyword(null);
      toast.success("Keyword deleted successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to delete keyword");
    } finally {
      setDeleting(false);
    }
  };

  const SkeletonRows = () => (
    <>
      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <tr key={i} className="bk-skeleton-row">
          <td>
            <span className="bk-skel bk-skel-40" />
          </td>
          <td>
            <span className="bk-skel bk-skel-140" />
          </td>
          <td>
            <span className="bk-skel bk-skel-80" />
          </td>
          <td>
            <span className="bk-skel bk-skel-140" />
          </td>
          <td>
            <span className="bk-skel bk-skel-140" />
          </td>
          <td className="center">
            <span className="bk-skel bk-skel-56" />
          </td>
        </tr>
      ))}
    </>
  );

  const ThCol = ({ col, label, center }) => (
    <th
      className={`sortable${center ? " center" : ""}`}
      onClick={() => handleSort(col)}
      title={`Sort by ${label}`}
    >
      <span className="bk-th-inner">
        {label}
        <span className="bk-sort-icon">{sortIcon(col)}</span>
      </span>
    </th>
  );

  return (
    <div className="bk-page">
      <div className="bk-header">
        <div className="bk-header-left">
          <h1>Block Keyword</h1>
          <p>{filtered.length} keyword{filtered.length !== 1 ? "s" : ""} found</p>
        </div>
        {hasPermission('CREATE_BLOCK_KEYWORD') && (
          <button
            type="button"
          className="bk-btn-add"
          onClick={() => {
            setSelectedKeyword(null);
            setModalMode("add");
          }}
        >
          + Add New Keyword
        </button>
        )}
      </div>

      <div className="bk-filters bk-filters-end">
        <div className="bk-filters-right">
          <select
            className="bk-filter-select bk-max-w-180"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
          >
            <option value="all">All Actions</option>
            <option value="Blocked">Blocked</option>
            <option value="Warn">Warn</option>
          </select>
          <div className="bk-search-wrap">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by id, keyword or action..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKey}
            />
          </div>
          <button type="button" className="bk-btn-search" onClick={handleSearch}>
            Search
          </button>
        </div>
      </div>

      <div className="bk-table-wrap">
        <table className="bk-table">
          <thead>
            <tr>
              <ThCol col="id" label="ID" />
              <ThCol col="keyword" label="Keyword" />
              <ThCol col="action_type" label="Action Type" />
              <ThCol col="created_at" label="Created At" />
              <ThCol col="updated_at" label="Updated At" />
              <th className="center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : paginatedKeywords.length === 0 ? (
              <tr className="bk-empty-row">
                <td colSpan={6}>
                  <div className="bk-empty-icon">No Data</div>
                  <h3>No keywords found</h3>
                  <p>Try adjusting your search or add a new keyword.</p>
                </td>
              </tr>
            ) : (
              paginatedKeywords.map((keyword) => (
                <tr key={keyword.id}>
                  <td>
                    <span className="bk-cell-id">#{keyword.id}</span>
                  </td>
                  <td>
                    <div className="bk-cell-name">{keyword.keyword}</div>
                  </td>
                  <td>
                    <span className={`bk-action-badge ${keyword.action_type === "Blocked" ? "blocked" : "flagged"}`}>
                      {keyword.action_type}
                    </span>
                  </td>
                  <td>
                    <span className="bk-date-text">
                      {formatISTDateTime(keyword.created_at)}
                    </span>
                  </td>
                  <td>
                    <span className="bk-date-text">
                      {formatISTDateTime(keyword.updated_at)}
                    </span>
                  </td>
                  <td className="center">
                    <div className="bk-actions-cell">
                      {hasPermission('MODIFY_BLOCK_KEYWORD') && (
                      <button
                        type="button"
                        className="bk-icon-btn"
                        title="Edit"
                        onClick={() => handleEditPrompt(keyword)}
                      >
                        <EditIcon />
                      </button>
                      )}
                      {hasPermission('DELETE_BLOCK_KEYWORD') && (
                      <button
                        type="button"
                        className="bk-icon-btn delete"
                        title="Delete"
                        onClick={() => handleDeletePrompt(keyword)}
                      >
                        <TrashIcon />
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {!loading && filtered.length > 0 && (
          <div className="bk-table-footer">
            <span>
              Showing {paginatedKeywords.length} of {filtered.length} keywords
            </span>

            {totalPages > 1 && (
              <div className="bk-pagination">
                <button
                  type="button"
                  className="bk-page-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                >
                  {"<"}
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        type="button"
                        key={page}
                        className={`bk-page-btn ${currentPage === page ? "active" : ""}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    );
                  }

                  if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={page} className="bk-pager-separator">
                        ...
                      </span>
                    );
                  }

                  return null;
                })}

                <button
                  type="button"
                  className="bk-page-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                >
                  {">"}
                </button>
              </div>
            )}

            <span className="bk-refresh-time">
              Last refreshed: {formatISTDateTime(new Date())}
            </span>
          </div>
        )}
      </div>

      {modalMode === "delete" && selectedKeyword && (
        <DeleteModal
          item={selectedKeyword}
          onClose={() => {
            if (!deleting) {
              setModalMode(null);
              setSelectedKeyword(null);
            }
          }}
          onConfirm={handleDeleteConfirm}
          submitting={deleting}
        />
      )}

      {(modalMode === "add" || modalMode === "edit") && (
        <AddEditModal
          mode={modalMode}
          initialData={selectedKeyword}
          onClose={() => {
            if (!saving) {
              setModalMode(null);
              setSelectedKeyword(null);
            }
          }}
          onSave={modalMode === "edit" ? handleUpdate : handleCreate}
          submitting={saving}
        />
      )}
    </div>
  );
};

export default BlockKeyword;
